/**
 * Design Insights Facade (100% Shared)
 * 
 * Manages design insights fetching and state for conversations.
 * 100% shared between web and plugin through dependency injection.
 * 
 * @module useDesignInsightsFacade
 * @category Facades/Shared
 */

import { useState, useCallback } from 'react';
import apiClient from '../../../api/client';
import { ApiUrls } from '../../../api/ApiUrls';

// ==================== Types ====================

export interface DesignInsight {
  title: string;
  description: string;
  solution: string;
}

export interface PainPoint {
  description: string;
}

export interface Persona {
  name: string;
}

export interface UIBestPractice {
  title: string;
  description: string;
}

export interface DesignInsightsResponse {
  insights: DesignInsight[];
  pain_points: PainPoint[];
  personas: Persona[];
  problem_statement: string;
  ui_best_practices: UIBestPractice[];
}

export interface DesignInsightsFacade {
  /**
   * Current insights data
   */
  insights: DesignInsightsResponse | null;

  /**
   * Loading state
   */
  isLoading: boolean;

  /**
   * Error state
   */
  error: string | null;

  /**
   * Fetch design insights for a conversation
   */
  fetchInsights: (projectId: string, conversationId: string) => Promise<void>;

  /**
   * Clear insights data
   */
  clearInsights: () => void;
}

/**
 * Design Insights Facade Hook
 * 
 * Provides design insights fetching and state management.
 */
export function useDesignInsightsFacade(): DesignInsightsFacade {
  const [insights, setInsights] = useState<DesignInsightsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch design insights for a conversation
   */
  const fetchInsights = useCallback(async (projectId: string, conversationId: string) => {
    if (!projectId || !conversationId) {
      setError('Project ID and Conversation ID are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const url = `${ApiUrls.createProject}/${projectId}/conversations/${conversationId}/insights`;
      const response = await apiClient.get<DesignInsightsResponse>(url);
      
      setInsights(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch design insights:', err);
      setError('Failed to fetch design insights');
      setInsights(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear insights data
   */
  const clearInsights = useCallback(() => {
    setInsights(null);
    setError(null);
  }, []);

  return {
    insights,
    isLoading,
    error,
    fetchInsights,
    clearInsights,
  };
}
